import { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { ARTIST_NAME, IMG, VIDEOS } from "@/data/content";
import {
  Reveal, ChapterLabel, MaskLines, ParallaxImage, CTALink, VideoLightbox, HorizontalGallery, usePageTitle,
} from "@/components/Shared";
import { Play, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { FaqList } from "@/pages/LessonsPage";
import { EventSummary } from "@/components/EventSummary";

const EVENT_VIDEO = VIDEOS.find((v) => v.id === "in-a-sunnymental-mood");

const EventMusicPage = () => {
  const { t, lp } = useLang();
  const [lightbox, setLightbox] = useState(null);
  usePageTitle(`${t.nav.events} — ${ARTIST_NAME}`);
  const gallery = [IMG.galaTable, IMG.eventVenue, IMG.galaLights, IMG.lounge];

  return (
    <div data-testid="eventmusic-page">
      {/* Luxury Hero */}
      <section className="relative flex min-h-[95vh] items-end overflow-hidden pt-32">
        <ParallaxImage src={IMG.events} alt="Premium Event" className="absolute inset-0 h-full" imgClass="opacity-45" speed={50} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] via-[#0B0B0C]/30 to-[#0B0B0C]/70" />
        <div className="relative z-10 w-full px-6 pb-20 sm:px-12 lg:px-20">
          <Reveal y={20}>
            <p className="mb-6 font-sans text-xs font-medium uppercase tracking-[0.35em] text-gold">{t.events.eyebrow}</p>
          </Reveal>
          <MaskLines as="h1" text={t.events.title} className="font-serif text-5xl font-light leading-[0.95] text-[#F9F8F6] sm:text-7xl lg:text-8xl" />
          <Reveal delay={0.4}>
            <p className="mt-8 max-w-2xl font-sans text-sm leading-relaxed text-[#A1A1AA] sm:text-base">{t.events.intro}</p>
          </Reveal>
          <Reveal delay={0.55} className="mt-10">
            <CTALink to={lp("/contact")} testid="events-request-cta">{t.common.requestAvailability}</CTALink>
          </Reveal>
        </div>
      </section>

      <EventSummary />

      {/* Event Types */}
      <section className="border-b border-white/5 px-6 py-20 sm:px-12 sm:py-28 lg:px-20">
        <Reveal><ChapterLabel no="02" label={t.events.typesLabel} /></Reveal>
        <div className="mt-10 flex flex-wrap gap-x-10 gap-y-5">
          {t.events.types.map((type, i) => (
            <Reveal key={type} delay={i * 0.05}>
              <span className="group inline-flex items-baseline gap-3">
                <span className="font-mono text-[10px] text-[#C5A059]/60">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-serif text-2xl font-light text-[#F9F8F6]/85 transition-colors duration-300 group-hover:text-[#C5A059] sm:text-4xl">{type}</span>
              </span>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Performance Concepts */}
      <section className="px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
        <Reveal><ChapterLabel no="03" label={t.events.conceptsLabel} /></Reveal>
        <div className="mt-12 space-y-0 border-t border-white/10">
          {t.events.concepts.map((c, i) => (
            <Reveal key={c.name} delay={i * 0.05}>
              <div data-testid={`concept-${i}`} className="group grid gap-4 border-b border-white/10 py-8 transition-colors duration-500 hover:bg-white/[0.02] sm:grid-cols-12 sm:items-baseline sm:gap-8 sm:px-4">
                <span className="font-mono text-xs tracking-[0.3em] text-[#C5A059] sm:col-span-1">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-serif text-3xl font-light text-[#F9F8F6] transition-colors duration-300 group-hover:text-[#C5A059] sm:col-span-4 sm:text-4xl">{c.name}</h3>
                <p className="max-w-xl font-sans text-sm leading-relaxed text-[#A1A1AA] sm:col-span-7">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* References */}
      <section className="relative overflow-hidden border-y border-white/5 bg-[#141416]">
        <div className="grid items-center gap-12 px-6 py-24 sm:px-12 sm:py-32 lg:grid-cols-12 lg:px-20">
          <div className="lg:col-span-6">
            <Reveal><ChapterLabel no="04" label={t.events.refsLabel} /></Reveal>
            <Reveal delay={0.1}>
              <p className="max-w-lg font-sans text-sm leading-loose text-[#A1A1AA]">{t.events.refs}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <ParallaxImage src={IMG.galaLights} alt="Gala" className="aspect-[4/3]" />
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
        <Reveal><ChapterLabel no="05" label={t.events.galleryLabel} /></Reveal>
        <div className="mt-12">
          <HorizontalGallery images={gallery} />
        </div>
      </section>

      {/* Video */}
      <section className="px-6 pb-24 sm:px-12 lg:px-20">
        <Reveal><ChapterLabel no="06" label={t.events.videoLabel} /></Reveal>
        <button
          data-testid="events-video-teaser"
          data-cursor="PLAY"
          onClick={() => setLightbox(EVENT_VIDEO)}
          className="group relative mt-8 block aspect-video w-full overflow-hidden"
        >
          <img src={EVENT_VIDEO.img} alt={EVENT_VIDEO.title} loading="lazy" className="h-full w-full object-cover opacity-70 transition-all duration-700 group-hover:scale-105 group-hover:opacity-100" />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full border border-[#C5A059]/60 bg-[#0B0B0C]/60 backdrop-blur transition-all duration-500 group-hover:bg-[#C5A059]">
              <Play className="ml-1 h-5 w-5 text-[#C5A059] group-hover:text-[#0B0B0C]" />
            </span>
          </div>
        </button>
      </section>

      {/* Process */}
      <section className="border-t border-white/5 px-6 py-24 sm:px-12 sm:py-32 lg:px-20" data-testid="events-area-section">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal><ChapterLabel no="07" label={t.events.areaLabel} /></Reveal>
            <Reveal delay={0.1}><p className="mt-8 max-w-md font-sans text-sm leading-loose text-[#A1A1AA]">{t.events.areaText}</p></Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 font-sans text-sm text-[#71717A]">
                <Link to={lp("/bands/quartett")} className="underline underline-offset-4 hover:text-[#C5A059]">{t.nav.quartett}</Link> · <Link to={lp("/bands/full-band")} className="underline underline-offset-4 hover:text-[#C5A059]">{t.nav.band}</Link> · <Link to={lp("/music/video")} className="underline underline-offset-4 hover:text-[#C5A059]">{t.nav.video}</Link> · <Link to={lp("/lessons")} className="underline underline-offset-4 hover:text-[#C5A059]">{t.nav.lessons}</Link>
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal><ChapterLabel no="08" label={t.events.faqLabel} /></Reveal>
            <FaqList items={t.events.faq} testPrefix="events" />
          </div>
        </div>
      </section>

      <section className="border-t border-white/5 bg-[#141416] px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
        <Reveal><ChapterLabel no="09" label={t.events.processLabel} /></Reveal>
        <div className="mt-12 grid gap-10 lg:grid-cols-3">
          {t.events.process.map((p, i) => (
            <Reveal key={p.step} delay={i * 0.12}>
              <div className="border-t border-[#C5A059]/30 pt-8">
                <span className="font-serif text-6xl font-light text-outline-gold">{p.step}</span>
                <h3 className="mt-5 font-serif text-2xl font-light text-[#F9F8F6]">{p.title}</h3>
                <p className="mt-3 font-sans text-sm leading-relaxed text-[#A1A1AA]">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.3} className="mt-16 text-center">
          <Link
            to={lp("/contact")}
            data-testid="events-book-cta"
            data-cursor="OPEN"
            className="group inline-flex items-center gap-4 border border-[#C5A059] px-12 py-5 font-sans text-xs font-medium uppercase tracking-[0.35em] text-[#C5A059] transition-all duration-500 hover:bg-[#C5A059] hover:text-[#0B0B0C]"
          >
            {t.common.bookEvent}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </Reveal>
      </section>

      <AnimatePresence>{lightbox && <VideoLightbox item={lightbox} onClose={() => setLightbox(null)} />}</AnimatePresence>
    </div>
  );
};

export default EventMusicPage;
